import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  getAuthToken,
  getCurrentShopId,
  getCurrentUserRole,
} from "./auth";
import "./PurchasesPage.css";

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000";

const getToday = () => {
  return new Date().toISOString().slice(0, 10);
};

const emptyForm = {
  purchase_type: "expense",
  supplier_id: "",
  category_id: "",
  reference_number: "",
  purchase_date: getToday(),
  net_amount: "",
  vat_amount: "0",
  payment_method: "cash",
  description: "",
  items: [],
};

const emptyItem = {
  product_id: "",
  quantity: "1",
  cost_price: "",
};

const paymentMethods = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "direct_debit", label: "Direct Debit" },
  { value: "credit_account", label: "Credit Account" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" },
];

function PurchasesPage() {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [form, setForm] = useState(emptyForm);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [editingPurchaseId, setEditingPurchaseId] =
    useState(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [referenceLoading, setReferenceLoading] =
    useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] =
    useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const token = getAuthToken();
  const shopId = getCurrentShopId();
  const role = getCurrentUserRole();

  const buildAuthConfig = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    if (role === "super_admin") {
      config.params = {
        shop_id: shopId,
      };
    }

    return config;
  };

const loadReferenceData = async () => {
  try {
    setReferenceLoading(true);
    setError("");

    if (!shopId) {
      throw new Error(
        "Shop ID was not found. Please log out and log in again."
      );
    }

    const supplierConfig = buildAuthConfig();
    const categoryConfig = buildAuthConfig();
    const productConfig = buildAuthConfig();

    /*
      Always send the currently logged-in shop ID.

      The backend must still verify this against the JWT,
      but this ensures the correct shop is requested.
    */
    supplierConfig.params = {
      ...(supplierConfig.params || {}),
      shop_id: shopId,
    };

    categoryConfig.params = {
      ...(categoryConfig.params || {}),
      shop_id: shopId,
    };

    productConfig.params = {
      ...(productConfig.params || {}),
      shop_id: shopId,
    };

    const [
      suppliersResponse,
      categoriesResponse,
      productsResponse,
    ] = await Promise.all([
      axios.get(`${API_URL}/suppliers`, supplierConfig),

      axios.get(
        `${API_URL}/purchase-categories`,
        categoryConfig
      ),

      axios.get(`${API_URL}/products`, productConfig),
    ]);

    const supplierData = Array.isArray(
      suppliersResponse.data
    )
      ? suppliersResponse.data
      : [];

    const categoryData = Array.isArray(
      categoriesResponse.data
    )
      ? categoriesResponse.data
      : [];

    const productData = Array.isArray(
      productsResponse.data
    )
      ? productsResponse.data
      : Array.isArray(productsResponse.data?.products)
        ? productsResponse.data.products
        : [];

    setSuppliers(
      supplierData.filter(
        (supplier) =>
          supplier.active !== false &&
          Number(supplier.shop_id) === Number(shopId)
      )
    );

    setCategories(
      categoryData.filter(
        (category) =>
          category.active !== false &&
          Number(category.shop_id) === Number(shopId)
      )
    );

    setProducts(
      productData.filter(
        (product) =>
          Number(product.shop_id) === Number(shopId)
      )
    );
  } catch (err) {
    console.error(
      "Load purchase reference data error:",
      err.response?.data || err.message
    );

    setProducts([]);
    setSuppliers([]);
    setCategories([]);

    setError(
      err.response?.data?.error ||
        err.message ||
        "Failed to load suppliers, categories or products"
    );
  } finally {
    setReferenceLoading(false);
  }
};

  const loadPurchases = async () => {
    try {
      setLoading(true);
      setError("");

      const config = buildAuthConfig();

      config.params = {
        ...(config.params || {}),
      };

      if (search.trim()) {
        config.params.search = search.trim();
      }

      if (typeFilter !== "all") {
        config.params.purchase_type = typeFilter;
      }

      if (supplierFilter) {
        config.params.supplier_id = supplierFilter;
      }

      if (categoryFilter) {
        config.params.category_id = categoryFilter;
      }

      if (dateFrom) {
        config.params.from = dateFrom;
      }

      if (dateTo) {
        config.params.to = dateTo;
      }

      const response = await axios.get(
        `${API_URL}/purchases`,
        config
      );

      setPurchases(
        Array.isArray(response.data) ? response.data : []
      );
    } catch (err) {
      console.error(
        "Load purchases error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error ||
          "Failed to load purchases"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferenceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadPurchases();
    }, 250);

    return () => clearTimeout(timeout);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    typeFilter,
    supplierFilter,
    categoryFilter,
    dateFrom,
    dateTo,
  ]);

  const availableCategories = useMemo(() => {
    return categories.filter(
      (category) =>
        category.purchase_type === form.purchase_type
    );
  }, [categories, form.purchase_type]);

  const availableFilterCategories = useMemo(() => {
    if (typeFilter === "all") {
      return categories;
    }

    return categories.filter(
      (category) => category.purchase_type === typeFilter
    );
  }, [categories, typeFilter]);

  const availableProducts = useMemo(() => {
  const selectedSupplierId = Number(form.supplier_id);

  if (!selectedSupplierId) {
    return products;
  }

  return products.filter(
    (product) =>
      Number(product.supplier_id) === selectedSupplierId
  );
}, [products, form.supplier_id]);

  const selectedProduct = useMemo(() => {
    return products.find(
      (product) =>
        Number(product.id) === Number(itemForm.product_id)
    );
  }, [products, itemForm.product_id]);

  const stockNetAmount = useMemo(() => {
    return form.items.reduce((total, item) => {
      return total + Number(item.line_total || 0);
    }, 0);
  }, [form.items]);

  const netAmount =
    form.purchase_type === "stock_purchase"
      ? stockNetAmount
      : Number(form.net_amount || 0);

  const vatAmount = Number(form.vat_amount || 0);

  const totalAmount = netAmount + vatAmount;

  const formatMoney = (value) => {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: "EUR",
    }).format(Number(value || 0));
  };

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }

    return new Date(`${value.slice(0, 10)}T00:00:00`)
      .toLocaleDateString("en-IE");
  };

  const formatPaymentMethod = (value) => {
    return (
      paymentMethods.find(
        (method) => method.value === value
      )?.label || value
    );
  };

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setFieldErrors((previous) => ({
      ...previous,
      [field]: "",
    }));
  };

 const changePurchaseType = (purchaseType) => {
  const firstCategory = categories.find(
    (category) =>
      category.purchase_type === purchaseType
  );

  setForm((previous) => ({
    ...previous,

    purchase_type: purchaseType,

    category_id: firstCategory
      ? String(firstCategory.id)
      : "",

    supplier_id:
      purchaseType === "stock_purchase"
        ? previous.supplier_id
        : "",

    items:
      purchaseType === "stock_purchase"
        ? previous.items
        : [],

    net_amount:
      purchaseType === "expense"
        ? previous.net_amount
        : "",
  }));

  setItemForm(emptyItem);
  setFieldErrors({});
};

  const openCreateModal = () => {
    const firstExpenseCategory = categories.find(
      (category) =>
        category.purchase_type === "expense"
    );

    setEditingPurchaseId(null);

    setForm({
      ...emptyForm,
      purchase_date: getToday(),
      category_id: firstExpenseCategory
        ? String(firstExpenseCategory.id)
        : "",
    });

    setItemForm(emptyItem);
    setFieldErrors({});
    setError("");
    setSuccessMessage("");
    setModalOpen(true);
  };

  const openEditModal = async (purchase) => {
    try {
      setError("");
      setSuccessMessage("");
      setReferenceLoading(true);

      const response = await axios.get(
        `${API_URL}/purchases/${purchase.id}`,
        buildAuthConfig()
      );

      const purchaseDetails = response.data;

      setEditingPurchaseId(purchase.id);

      setForm({
        purchase_type:
          purchaseDetails.purchase_type || "expense",

        supplier_id: purchaseDetails.supplier_id
          ? String(purchaseDetails.supplier_id)
          : "",

        category_id: purchaseDetails.category_id
          ? String(purchaseDetails.category_id)
          : "",

        reference_number:
          purchaseDetails.reference_number || "",

        purchase_date:
          purchaseDetails.purchase_date?.slice(0, 10) ||
          getToday(),

        net_amount:
          purchaseDetails.purchase_type === "expense"
            ? String(purchaseDetails.net_amount || "")
            : "",

        vat_amount: String(
          purchaseDetails.vat_amount || "0"
        ),

        payment_method:
          purchaseDetails.payment_method || "cash",

        description: purchaseDetails.description || "",

        items: Array.isArray(purchaseDetails.items)
          ? purchaseDetails.items.map((item) => ({
              product_id: item.product_id,
              product_name: item.product_name,
              quantity: Number(item.quantity),
              cost_price: Number(item.cost_price),
              line_total: Number(item.line_total),
            }))
          : [],
      });

      setItemForm(emptyItem);
      setFieldErrors({});
      setModalOpen(true);
    } catch (err) {
      console.error(
        "Load purchase details error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error ||
          "Failed to load purchase details"
      );
    } finally {
      setReferenceLoading(false);
    }
  };

  const closeModal = () => {
    if (saving) {
      return;
    }

    setModalOpen(false);
    setEditingPurchaseId(null);
    setForm(emptyForm);
    setItemForm(emptyItem);
    setFieldErrors({});
  };

  const addPurchaseItem = () => {
    const errors = {};

    if (!itemForm.product_id) {
      errors.product_item = "Select a product";
    }

    const quantity = Number(itemForm.quantity);
    const costPrice = Number(itemForm.cost_price);

    if (!Number.isFinite(quantity) || quantity <= 0) {
      errors.product_item =
        "Quantity must be greater than zero";
    }

    if (!Number.isFinite(costPrice) || costPrice < 0) {
      errors.product_item =
        "Enter a valid product cost price";
    }

    const alreadyAdded = form.items.some(
      (item) =>
        Number(item.product_id) ===
        Number(itemForm.product_id)
    );

    if (alreadyAdded) {
      errors.product_item =
        "This product has already been added";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors((previous) => ({
        ...previous,
        ...errors,
      }));

      return;
    }

    const lineTotal = quantity * costPrice;

    setForm((previous) => ({
      ...previous,
      items: [
        ...previous.items,
        {
          product_id: Number(itemForm.product_id),
          product_name:
            selectedProduct?.name || "Product",
          quantity,
          cost_price: costPrice,
          line_total: lineTotal,
        },
      ],
    }));

    setItemForm(emptyItem);

    setFieldErrors((previous) => ({
      ...previous,
      product_item: "",
    }));
  };

  const removePurchaseItem = (productId) => {
    setForm((previous) => ({
      ...previous,
      items: previous.items.filter(
        (item) =>
          Number(item.product_id) !== Number(productId)
      ),
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!form.purchase_type) {
      errors.purchase_type =
        "Purchase type is required";
    }

    if (!form.category_id) {
      errors.category_id =
        "Purchase category is required";
    }

    if (!form.purchase_date) {
      errors.purchase_date =
        "Purchase date is required";
    }

    if (!form.payment_method) {
      errors.payment_method =
        "Payment method is required";
    }

    if (form.purchase_type === "expense") {
      const expenseNetAmount = Number(form.net_amount);

      if (
        !Number.isFinite(expenseNetAmount) ||
        expenseNetAmount < 0
      ) {
        errors.net_amount =
          "Enter a valid net amount";
      }
    }

    if (
      form.purchase_type === "stock_purchase" &&
      form.items.length === 0
    ) {
      errors.items =
        "Add at least one product to the stock purchase";
    }

    const purchaseVat = Number(form.vat_amount);

    if (
      !Number.isFinite(purchaseVat) ||
      purchaseVat < 0
    ) {
      errors.vat_amount =
        "Enter a valid VAT amount";
    }

    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const savePurchase = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const payload = {
        purchase_type: form.purchase_type,

        supplier_id: form.supplier_id
          ? Number(form.supplier_id)
          : null,

        category_id: Number(form.category_id),

        reference_number:
          form.reference_number.trim() || null,

        purchase_date: form.purchase_date,

        net_amount:
          form.purchase_type === "expense"
            ? Number(form.net_amount || 0)
            : undefined,

        vat_amount: Number(form.vat_amount || 0),

        payment_method: form.payment_method,

        description: form.description.trim() || null,

        items:
          form.purchase_type === "stock_purchase"
            ? form.items.map((item) => ({
                product_id: Number(item.product_id),
                quantity: Number(item.quantity),
                cost_price: Number(item.cost_price),
              }))
            : [],
      };

      if (role === "super_admin") {
        payload.shop_id = shopId;
      }

      if (editingPurchaseId) {
        await axios.put(
          `${API_URL}/purchases/${editingPurchaseId}`,
          payload,
          buildAuthConfig()
        );

        setSuccessMessage(
          "Purchase updated successfully"
        );
      } else {
        await axios.post(
          `${API_URL}/purchases`,
          payload,
          buildAuthConfig()
        );

        setSuccessMessage(
          "Purchase created successfully"
        );
      }

      closeModal();
      await loadPurchases();
    } catch (err) {
      console.error(
        "Save purchase error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error ||
          "Failed to save purchase"
      );
    } finally {
      setSaving(false);
    }
  };

  const deletePurchase = async (purchase) => {
    const warning =
      purchase.purchase_type === "stock_purchase"
        ? `Delete "${purchase.reference_number || "this stock purchase"}"?\n\nThis will remove its purchased quantities from the current product stock.`
        : `Delete "${purchase.reference_number || "this expense"}"?`;

    const confirmed = window.confirm(warning);

    if (!confirmed) {
      return;
    }

    try {
      setError("");
      setSuccessMessage("");

      await axios.delete(
        `${API_URL}/purchases/${purchase.id}`,
        buildAuthConfig()
      );

      setSuccessMessage(
        "Purchase deleted successfully"
      );

      await loadPurchases();
    } catch (err) {
      console.error(
        "Delete purchase error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error ||
          "Failed to delete purchase"
      );
    }
  };


  const downloadPurchasesCsv = () => {
  if (purchases.length === 0) {
    alert("There are no purchases to download");
    return;
  }

  const escapeCsvValue = (value) => {
    const text = String(value ?? "");

    return `"${text.replace(/"/g, '""')}"`;
  };

  const headers = [
    "Purchase Date",
    "Reference",
    "Supplier",
    "Description",
    "Category",
    "Purchase Type",
    "Net Amount",
    "VAT Amount",
    "Total Amount",
    "Payment Method",
  ];

  const rows = purchases.map((purchase) => [
    purchase.purchase_date
  ? `="${formatDate(purchase.purchase_date)}"`
  : "",

    purchase.reference_number || "",

    purchase.supplier_name || "",

    purchase.description || "",

    purchase.category_name ||
      purchase.category ||
      "",

    purchase.purchase_type === "stock_purchase"
      ? "Stock Purchase"
      : "Business Expense",

    Number(purchase.net_amount || 0).toFixed(2),

    Number(purchase.vat_amount || 0).toFixed(2),

    Number(purchase.total_amount || 0).toFixed(2),

    formatPaymentMethod(purchase.payment_method),
  ]);

  const csvContent = [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) =>
      row.map(escapeCsvValue).join(",")
    ),
  ].join("\r\n");

  /*
    UTF-8 BOM helps Microsoft Excel display
    symbols and accented text correctly.
  */
  const csvBlob = new Blob(
    ["\uFEFF", csvContent],
    {
      type: "text/csv;charset=utf-8;",
    }
  );

  const downloadUrl = URL.createObjectURL(csvBlob);
  const downloadLink = document.createElement("a");

  const today = new Date()
    .toISOString()
    .slice(0, 10);

  downloadLink.href = downloadUrl;
  downloadLink.download =
    `deskpos-purchases-${today}.csv`;

  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);

  URL.revokeObjectURL(downloadUrl);
};


  const clearFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setSupplierFilter("");
    setCategoryFilter("");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <div className="purchases-page">
      <div className="purchases-page-header">
        <div>
          <h1>Purchases</h1>

          <p className="purchases-subtitle">
            Record stock purchases and business expenses.
          </p>
        </div>

       <div className="purchases-header-actions">
  <button
    type="button"
    className="purchase-download-btn"
    onClick={downloadPurchasesCsv}
    disabled={loading || purchases.length === 0}
  >
    ↓ Download Purchases
  </button>

  <button
    type="button"
    className="purchase-primary-btn"
    onClick={openCreateModal}
    disabled={referenceLoading}
  >
    + New Purchase
  </button>
</div>
      </div>

      {error && (
        <div className="purchase-alert purchase-alert-error">{error}</div>
      )}

      {successMessage && (
        <div className="purchase-alert purchase-alert-success">
          {successMessage}
        </div>
      )}

      <div className="purchase-toolbar">
        <div className="purchase-search-wrap">
          <span className="purchase-search-icon">⌕</span>

          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search reference, supplier or description..."
          />
        </div>

        <div className="purchase-filter-grid">
          <select
            value={typeFilter}
            onChange={(event) => {
              setTypeFilter(event.target.value);
              setCategoryFilter("");
            }}
          >
            <option value="all">All types</option>
            <option value="stock_purchase">Stock purchases</option>
            <option value="expense">Business expenses</option>
          </select>

          <select
            value={supplierFilter}
            onChange={(event) => setSupplierFilter(event.target.value)}
          >
            <option value="">All suppliers</option>

            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
          >
            <option value="">All categories</option>

            {availableFilterCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            aria-label="Date from"
          />

          <input
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            aria-label="Date to"
          />

          <button
            type="button"
            className="purchase-clear-filter-btn"
            onClick={clearFilters}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="purchase-table-card">
        {loading ? (
          <div className="purchase-state">Loading purchases...</div>
        ) : purchases.length === 0 ? (
          <div className="purchase-empty-state">
            <div className="purchase-empty-icon">P</div>

            <h2>No purchases found</h2>

            <p>Record your first stock purchase or business expense.</p>

            <button
              type="button"
              className="purchase-primary-btn"
              onClick={openCreateModal}
            >
              + Add First Purchase
            </button>
          </div>
        ) : (
          <>
            <div className="purchase-result-count">
              {purchases.length}{" "}
              {purchases.length === 1 ? "purchase" : "purchases"}
            </div>

            <div className="purchase-table-scroll">
              <table className="purchase-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Reference</th>
                    <th>Supplier / Description</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Total</th>
                    <th>Payment</th>
                    <th className="purchase-actions-heading">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {purchases.map((purchase) => (
                    <tr key={purchase.id}>
                      <td data-label="Date">
                        {formatDate(purchase.purchase_date)}
                      </td>

                      <td data-label="Reference">
                        <strong>{purchase.reference_number || "-"}</strong>
                      </td>

                      <td data-label="Supplier">
                        <div className="purchase-main-cell">
                          <strong>
                            {purchase.supplier_name || "No supplier"}
                          </strong>

                          <span>
                            {purchase.description || "No description"}
                          </span>
                        </div>
                      </td>

                      <td data-label="Category">
                        {purchase.category_name || purchase.category || "-"}
                      </td>

                      <td data-label="Type">
                        <span
                          className={`purchase-type-badge ${
                            purchase.purchase_type === "stock_purchase"
                              ? "stock"
                              : "expense"
                          }`}
                        >
                          {purchase.purchase_type === "stock_purchase"
                            ? "Stock Purchase"
                            : "Business Expense"}
                        </span>
                      </td>

                      <td data-label="Total">
                        <strong>{formatMoney(purchase.total_amount)}</strong>
                      </td>

                      <td data-label="Payment">
                        {formatPaymentMethod(purchase.payment_method)}
                      </td>

                      <td
                        data-label="Actions"
                        className="purchase-actions-cell"
                      >
                        <button
                          type="button"
                          className="purchase-action-btn"
                          onClick={() => openEditModal(purchase)}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className="purchase-action-btn purchase-action-danger"
                          onClick={() => deletePurchase(purchase)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {modalOpen && (
        <div
          className="purchase-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeModal();
            }
          }}
        >
          <div
            className="purchase-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="purchase-modal-title"
          >
            <div className="purchase-modal-header">
              <div>
                <h2 id="purchase-modal-title">
                  {editingPurchaseId ? "Edit Purchase" : "New Purchase"}
                </h2>

                <p>Record stock received or a business expense.</p>
              </div>

              <button
                type="button"
                className="purchase-modal-close"
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form className="purchase-form" onSubmit={savePurchase}>
              <div className="purchase-form-body">
                <section className="purchase-form-section">
                  <div className="purchase-section-heading">
                    <h3>Purchase type</h3>

                    <p>
                      Select whether this entry is a stock purchase or business
                      expense.
                    </p>
                  </div>

                  <div className="purchase-type-selector">
                    <button
                      type="button"
                      className={
                        form.purchase_type === "stock_purchase" ? "active" : ""
                      }
                      onClick={() => changePurchaseType("stock_purchase")}
                    >
                      Stock Purchase
                    </button>

                    <button
                      type="button"
                      className={
                        form.purchase_type === "expense" ? "active" : ""
                      }
                      onClick={() => changePurchaseType("expense")}
                    >
                      Business Expense
                    </button>
                  </div>
                </section>

                <section className="purchase-form-section">
                  <div className="purchase-section-heading">
                    <h3>Purchase details</h3>

                    <p>
                      Supplier, category, invoice reference and payment
                      information.
                    </p>
                  </div>

                  <div className="purchase-form-grid">
                    {form.purchase_type === "stock_purchase" && (
                      <div className="purchase-field">
                        <label htmlFor="purchase-supplier">Supplier</label>

                        <select
                          id="purchase-supplier"
                          value={form.supplier_id}
                          onChange={(event) => {
                            const supplierId = event.target.value;

                            setForm((previous) => ({
                              ...previous,
                              supplier_id: supplierId,
                              items: [],
                            }));

                            setItemForm(emptyItem);

                            setFieldErrors((previous) => ({
                              ...previous,
                              supplier_id: "",
                              items: "",
                              product_item: "",
                            }));
                          }}
                        >
                          <option value="">No supplier</option>

                          {suppliers.map((supplier) => (
                            <option key={supplier.id} value={supplier.id}>
                              {supplier.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="purchase-field">
                      <label htmlFor="purchase-category">
                        Category <span>*</span>
                      </label>

                      <select
                        id="purchase-category"
                        value={form.category_id}
                        onChange={(event) =>
                          updateField("category_id", event.target.value)
                        }
                        className={fieldErrors.category_id ? "input-error" : ""}
                      >
                        <option value="">Select category</option>

                        {availableCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>

                      {fieldErrors.category_id && (
                        <small className="purchase-field-error">
                          {fieldErrors.category_id}
                        </small>
                      )}
                    </div>

                    <div className="purchase-field">
                      <label htmlFor="purchase-reference">
                        Invoice / Reference
                      </label>

                      <input
                        id="purchase-reference"
                        type="text"
                        maxLength={100}
                        value={form.reference_number}
                        onChange={(event) =>
                          updateField("reference_number", event.target.value)
                        }
                        placeholder="Example: INV-10025"
                      />
                    </div>

                    <div className="purchase-field">
                      <label htmlFor="purchase-date">
                        Purchase Date <span>*</span>
                      </label>

                      <input
                        id="purchase-date"
                        type="date"
                        value={form.purchase_date}
                        onChange={(event) =>
                          updateField("purchase_date", event.target.value)
                        }
                        className={
                          fieldErrors.purchase_date ? "input-error" : ""
                        }
                      />
                    </div>

                    <div className="purchase-field">
                      <label htmlFor="purchase-payment">
                        Payment Method <span>*</span>
                      </label>

                      <select
                        id="purchase-payment"
                        value={form.payment_method}
                        onChange={(event) =>
                          updateField("payment_method", event.target.value)
                        }
                      >
                        {paymentMethods.map((method) => (
                          <option key={method.value} value={method.value}>
                            {method.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="purchase-field purchase-field-full">
                      <label htmlFor="purchase-description">
                        Description / Notes
                      </label>

                      <textarea
                        id="purchase-description"
                        rows="3"
                        value={form.description}
                        onChange={(event) =>
                          updateField("description", event.target.value)
                        }
                        placeholder="Internal notes about this purchase..."
                      />
                    </div>
                  </div>
                </section>

                {form.purchase_type === "stock_purchase" && (
                  <section className="purchase-form-section">
                    <div className="purchase-section-heading">
                      <h3>Products purchased</h3>

                      <p>
                        Add the products received, quantities and unit cost
                        prices.
                      </p>
                    </div>

                    <div className="purchase-item-entry">
                      <div className="purchase-field">
                        <label>Product</label>

                        <select
                          value={itemForm.product_id}
                          disabled={availableProducts.length === 0}
                          onChange={(event) => {
                            const selectedProductId = event.target.value;

                            const product = availableProducts.find(
                              (item) =>
                                Number(item.id) === Number(selectedProductId),
                            );

                            setItemForm((previous) => ({
                              ...previous,
                              product_id: selectedProductId,
                              cost_price:
                                product?.cost_price !== undefined &&
                                product?.cost_price !== null
                                  ? String(product.cost_price)
                                  : "",
                            }));
                          }}
                        >
                          <option value="">
                            {form.supplier_id && availableProducts.length === 0
                              ? "No products assigned to this supplier"
                              : "Select product"}
                          </option>

                          {availableProducts.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name}
                              {product.barcode ? ` — ${product.barcode}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="purchase-field">
                        <label>Quantity</label>

                        <input
                          type="number"
                          min="0.001"
                          step="0.001"
                          value={itemForm.quantity}
                          onChange={(event) =>
                            setItemForm((previous) => ({
                              ...previous,
                              quantity: event.target.value,
                            }))
                          }
                        />
                      </div>

                      <div className="purchase-field">
                        <label>Unit Cost (€)</label>

                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={itemForm.cost_price}
                          onChange={(event) =>
                            setItemForm((previous) => ({
                              ...previous,
                              cost_price: event.target.value,
                            }))
                          }
                        />
                      </div>

                      <button
                        type="button"
                        className="purchase-add-item-btn"
                        onClick={addPurchaseItem}
                      >
                        Add Product
                      </button>
                    </div>

                    {fieldErrors.product_item && (
                      <small className="purchase-field-error purchase-item-error">
                        {fieldErrors.product_item}
                      </small>
                    )}

                    {fieldErrors.items && (
                      <small className="purchase-field-error purchase-item-error">
                        {fieldErrors.items}
                      </small>
                    )}

                    {form.items.length > 0 && (
                      <div className="purchase-items-table-wrap">
                        <table className="purchase-items-table">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Quantity</th>
                              <th>Unit Cost</th>
                              <th>Total</th>
                              <th></th>
                            </tr>
                          </thead>

                          <tbody>
                            {form.items.map((item) => (
                              <tr key={item.product_id}>
                                <td>{item.product_name}</td>

                                <td>{item.quantity}</td>

                                <td>{formatMoney(item.cost_price)}</td>

                                <td>{formatMoney(item.line_total)}</td>

                                <td>
                                  <button
                                    type="button"
                                    className="purchase-remove-item-btn"
                                    onClick={() =>
                                      removePurchaseItem(item.product_id)
                                    }
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                )}

                <section className="purchase-form-section">
                  <div className="purchase-section-heading">
                    <h3>Purchase totals</h3>

                    <p>
                      Enter the net and VAT amounts. The total is calculated
                      automatically.
                    </p>
                  </div>

                  <div className="purchase-total-grid">
                    <div className="purchase-field">
                      <label htmlFor="purchase-net">Net Amount</label>

                      <input
                        id="purchase-net"
                        type="number"
                        min="0"
                        step="0.01"
                        value={
                          form.purchase_type === "stock_purchase"
                            ? stockNetAmount.toFixed(2)
                            : form.net_amount
                        }
                        onChange={(event) =>
                          updateField("net_amount", event.target.value)
                        }
                        disabled={form.purchase_type === "stock_purchase"}
                        className={fieldErrors.net_amount ? "input-error" : ""}
                      />

                      {fieldErrors.net_amount && (
                        <small className="purchase-field-error">
                          {fieldErrors.net_amount}
                        </small>
                      )}
                    </div>

                    <div className="purchase-field">
                      <label htmlFor="purchase-vat">VAT Amount</label>

                      <input
                        id="purchase-vat"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.vat_amount}
                        onChange={(event) =>
                          updateField("vat_amount", event.target.value)
                        }
                        className={fieldErrors.vat_amount ? "input-error" : ""}
                      />
                    </div>

                    <div className="purchase-total-display">
                      <span>Total</span>

                      <strong>{formatMoney(totalAmount)}</strong>
                    </div>
                  </div>
                </section>
              </div>

              <div className="purchase-modal-footer">
                <button
                  type="button"
                  className="purchase-secondary-btn"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="purchase-primary-btn"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingPurchaseId
                      ? "Update Purchase"
                      : "Save Purchase"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PurchasesPage;